/**
 * 🎛️ EXECUTIVE HEADER
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Header con título, descripción y actions menu (legacy actions integration)
 */

import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Edit, Copy, FolderOpen, Trash2 } from 'lucide-react';
import type { ExecutiveDashboard } from '../types';

interface ExecutiveHeaderProps {
  dashboard: ExecutiveDashboard;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
}

const ExecutiveHeader: React.FC<ExecutiveHeaderProps> = ({
  dashboard,
  onEdit,
  onDuplicate,
  onMove,
  onDelete
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="executive-header">
      <div className="executive-header-content">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <h1 className="executive-title">{dashboard.title}</h1>
            {dashboard.description && (
              <p className="executive-description">{dashboard.description}</p>
            )}
          </div>

          <div style={{ position: 'relative', marginLeft: 24 }} ref={menuRef}>
            <button
              className="icon-button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Dashboard actions"
            >
              <MoreVertical size={20} />
            </button>

            {menuOpen && (
              <div className="actions-menu">
                {onEdit && (
                  <button className="menu-item" onClick={() => { onEdit(); setMenuOpen(false); }}>
                    <Edit size={16} />
                    <span>Edit Dashboard</span>
                  </button>
                )}
                {onDuplicate && (
                  <button className="menu-item" onClick={() => { onDuplicate(); setMenuOpen(false); }}>
                    <Copy size={16} />
                    <span>Duplicate</span>
                  </button>
                )}
                {onMove && (
                  <button className="menu-item" onClick={() => { onMove(); setMenuOpen(false); }}>
                    <FolderOpen size={16} />
                    <span>Move to Folder</span>
                  </button>
                )}
                {onDelete && (
                  <>
                    <div className="menu-divider" />
                    <button className="menu-item menu-item-danger" onClick={() => { onDelete(); setMenuOpen(false); }}>
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Backend decisions (dev only) */}
        {dashboard.presentation?.decisions && process.env.NODE_ENV === 'development' && (
          <details className="executive-decisions">
            <summary>▸ Backend Decisions (dev only)</summary>
            <ul>
              {Object.entries(dashboard.presentation.decisions).map(([key, value]) => (
                <li key={key}>{key}: {JSON.stringify(value)}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
};

export default ExecutiveHeader;
